import React, { useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-balham.css';
import useColdefManageNews from '../../../hooks/grid/coldef/useColdefManageNews';
import commonColDef from '../../../data/grid/commonColDef';
import ActionsManageNews from '../../cellRenderers/ActionsManageNews';
import useGridOptionsManageNews from '../../../hooks/grid/gridOptions/useGridOptionsManageNews';
import BooleanRenderer from '../../cellRenderers/BooleanRenderer';
import UserNameFromEmailCellRenderer from '../../cellRenderers/UserNameFromEmailCellRenderer';
import { useTranslation } from 'react-i18next';
import { RootState, useDispatch, useSelector } from '../../../redux/store';
import { setDialogMode, openDialog, selectNews } from '../../../redux/slices/news';
import type { ColDef, ColumnApi, GridApi, GridReadyEvent, RowDoubleClickedEvent } from 'ag-grid-community';
import type { NewsType } from '../../../types/news';
import type { AgGridReact as AgGridReactType } from 'ag-grid-react/lib/agGridReact';

const GridNews = React.forwardRef<AgGridReactType>((props, ref): React.ReactElement => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { isLoading, newssForManage } = useSelector((state: RootState) => state.news);
  const { users } = useSelector((state: RootState) => state.user);
  const [gridApi, setGridApi] = useState<GridApi | undefined>(undefined);
  const [gridColumnApi, setGridColumnApi] = useState<ColumnApi | undefined>(undefined);
  const { gridOptions } = useGridOptionsManageNews();
  const customColDef: ColDef = {
    cellClass: 'cell',
  };
  const defaultColDef: ColDef = Object.assign({}, commonColDef, customColDef);
  const colDef = useColdefManageNews(t);
  const frameworkComponents = {
    actionsRenderer: ActionsManageNews,
    booleanRenderer: BooleanRenderer,
    userNameFromEmailCellRenderer: UserNameFromEmailCellRenderer,
  };

  // SIDE EFFECT
  // ================================================================================

  // 確認用
  // useEffect(() => {
  // }, []);

  /**
   * Loading, NoRowsのグリッド表示
   */
  useEffect(() => {
    if (gridApi) {
      if (isLoading) {
        gridApi.showLoadingOverlay();
      } else {
        if (newssForManage?.length) {
          gridApi.hideOverlay();
        } else {
          gridApi.showNoRowsOverlay();
        }
      }
    } else {
      // do nothing
    }
  }, [gridApi, isLoading, newssForManage]);

  useEffect(() => {
    if (gridApi && gridColumnApi) {
      const columnKeys: string[] = ['code', 'updatedAt', 'updatedBy', 'createdAt', 'createdBy'];
      gridColumnApi.autoSizeColumns(columnKeys);
      gridApi.redrawRows();
    }
  }, [gridColumnApi, newssForManage]);

  // EVENT HANDLER
  // ================================================================================

  /**
   * グリッドが準備できたらAPIを利用できるようにする
   * @param params GridReadyEvent
   */
  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  /**
   * ダブルクリックで編集モードにする
   * @param params GridReadyEvent
   */
  const onRowDoubleClicked = (event: RowDoubleClickedEvent) => {
    const data: NewsType = event.data;
    dispatch(selectNews(data));
    dispatch(setDialogMode('UPDATE'));
    dispatch(openDialog());
  };

  // VIEW
  // ================================================================================

  return (
    <div className="ag-theme-balham" style={{ height: '40vh' }}>
      <AgGridReact
        ref={ref}
        immutableData={true}
        gridOptions={gridOptions}
        defaultColDef={defaultColDef}
        columnDefs={colDef}
        rowData={newssForManage}
        onGridReady={onGridReady}
        onRowDoubleClicked={onRowDoubleClicked}
        frameworkComponents={frameworkComponents}
        // filterValueGetterで使うデータ
        context={{
          // reduxにあるものはコピーを渡す
          users: users?.map((user) => structuredClone(user)),
        }}
      />
    </div>
  );
});

// forwardRefの場合にはlinterに要求される場合がある
// https://stackoverflow.com/questions/67992894/component-definition-is-missing-display-name-for-forwardref
GridNews.displayName = 'GridNews';
export default GridNews;
